import { useIntl } from 'react-intl'

/**
 * How a storefront's accepted payment methods read to a buyer, shared by the
 * listing page's "How you'll pay" panel and the first-win modal so the same
 * configuration never reads two different ways.
 */
const ns = 'Listing.sellerCollects.paymentMethods'

/**
 * The storefront stores these free-form, so several spellings land on one
 * label and anything unrecognised is passed through rather than dropped — a
 * seller who accepts something this build has not heard of still gets it named.
 */
const LABEL_KEYS: Record<string, string> = {
  WIRE: 'wire',
  WIRE_TRANSFER: 'wire',
  BANK_WIRE: 'wire',
  // Not BANK_TRANSFER: ACH is a bank transfer too, so it is shown as written rather than claimed as wire.
  ACH: 'ach',
  AMEX: 'amex',
  CHECK: 'check',
  CREDIT_CARD: 'creditCard',
  REP: 'sellerRep',
  REPRESENTATIVE: 'sellerRep',
  SELLER_REP: 'sellerRep',
  ACCOUNT_REP: 'sellerRep',
}

const labelKeyFor = (method: string): string | undefined =>
  LABEL_KEYS[
    method
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_')
  ]

/**
 * Whether the storefront settles through an account representative rather than
 * a payment channel the buyer operates themselves.
 *
 * Read from the same free-form list the labels come from, so a seller whose
 * seed names the rep in any of its accepted spellings reads the same here.
 */
export const paysViaSellerRep = (methods: string[] | undefined): boolean =>
  (methods ?? []).some((method) => labelKeyFor(method) === 'sellerRep')

/**
 * Whether every method the storefront accepts is a wire, in any of the
 * spellings that label as one. An empty or absent list says nothing about how
 * the buyer pays, so it never reads as wire-only.
 */
export const paysOnlyByWire = (methods: string[] | undefined): boolean =>
  methods !== undefined &&
  methods.length > 0 &&
  methods.every((method) => labelKeyFor(method) === 'wire')

/**
 * An unrecognised method, in the register of the labels around it.
 *
 * The known labels are lowercase prose ("wire transfer"), so an enum-shaped
 * `BITCOIN` would shout mid-sentence. A seller who typed `PayPal` or `Zelle`
 * chose that spelling, though, so only an all-uppercase value is lowered.
 * An all-uppercase acronym such as `USDC` is indistinguishable from a shouted
 * word and is lowered too; add it to `LABEL_KEYS` if that ever matters.
 */
const passThrough = (method: string): string => {
  const trimmed = method.trim()
  return trimmed === trimmed.toUpperCase() ? trimmed.toLowerCase() : trimmed
}

/** `["WIRE", "AMEX"]` → `wire transfer or American Express`. */
export const useAcceptedPaymentMethods = (
  methods: string[] | undefined
): string | undefined => {
  const intl = useIntl()

  const labels = [
    ...new Set(
      (methods ?? [])
        .map((method) => {
          const key = labelKeyFor(method)
          return key
            ? intl.formatMessage({ id: `${ns}.${key}` })
            : passThrough(method)
        })
        .filter(Boolean)
    ),
  ]

  if (labels.length === 0) return undefined

  // Still in browserslist without `Intl.ListFormat`: Opera Mini and KaiOS 2.5.
  // A plain join reads worse than "or" but beats throwing mid-render.
  if (typeof Intl.ListFormat !== 'function') return labels.join(', ')

  // "or", and where the comma goes, are the locale's business, not ours.
  return new Intl.ListFormat(intl.locale, { type: 'disjunction' }).format(
    labels
  )
}
